/**
 * Export / import of individual workflow steps (DAG nodes) between workflows.
 *
 * Steps refer to each other only through text: dot-paths such as
 * "node_results.node_3.output.rows" (input mappings, write fields, edge
 * conditions) and, inside run_python code, node_results["node_3"]. On import
 * every step gets a fresh id from the target workflow and all such references
 * to other *imported* steps are rewritten. References to steps that were not
 * imported are left alone and reported, because in the target workflow they
 * point at nothing, or at an unrelated step that happens to share the id.
 */
import type { DAGEdge, DAGNode, SecretRef } from '../services/WorkflowService';

export const STEPS_EXPORT_FORMAT = 'catax-workflow-steps';

export interface StepsExport {
  format: typeof STEPS_EXPORT_FORMAT;
  version: 1;
  exported_at: string;
  source_workflow?: string;
  nodes: DAGNode[];      // positions relative to the top-left exported step
  edges: DAGEdge[];      // only edges between exported steps
  secrets: SecretRef[];  // Secret Manager references the steps use (no values)
}

type Pos = { x: number; y: number };

// node_results.<id>  (not a method call such as node_results.get(...))
const DOT_REF = /node_results\.(?!(?:get|keys|values|items|copy)\b)([A-Za-z0-9_-]+)(?![A-Za-z0-9_-]|\s*\()/g;
// node_results["<id>"], context["node_results"]["<id>"], node_results.get('<id>'), …
const KEY_REF = /(node_results(?:["']\s*\])?\s*(?:\[\s*|\.get\(\s*))(["'])([A-Za-z0-9_-]+)\2/g;
const SECRET_REF = /secrets\.([A-Za-z0-9_-]+)/g;

/** Apply fn to every string inside a JSON-like value (returns a new value). */
function mapStrings(value: any, fn: (s: string) => string): any {
  if (typeof value === 'string') return fn(value);
  if (Array.isArray(value)) return value.map(v => mapStrings(v, fn));
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) out[k] = mapStrings(v, fn);
    return out;
  }
  return value;
}

/** Every step id referenced anywhere in value. */
export function referencedNodeIds(value: any): Set<string> {
  const ids = new Set<string>();
  mapStrings(value, s => {
    for (const m of Array.from(s.matchAll(DOT_REF))) ids.add(m[1]);
    for (const m of Array.from(s.matchAll(KEY_REF))) ids.add(m[3]);
    return s;
  });
  return ids;
}

function referencedSecretNames(value: any): Set<string> {
  const names = new Set<string>();
  mapStrings(value, s => {
    for (const m of Array.from(s.matchAll(SECRET_REF))) names.add(m[1]);
    return s;
  });
  return names;
}

/** Rewrite step references in value using idMap; ids not in the map are untouched. */
export function rewriteNodeRefs(value: any, idMap: Record<string, string>): any {
  return mapStrings(value, s =>
    s
      .replace(DOT_REF, (whole, id) => (idMap[id] ? `node_results.${idMap[id]}` : whole))
      .replace(KEY_REF, (whole, pre, q, id) => (idMap[id] ? `${pre}${q}${idMap[id]}${q}` : whole)),
  );
}

/** Drop editor-only keys the backend never needs. */
function cleanConfig(config: Record<string, any> = {}): Record<string, any> {
  const { position: _p, ...rest } = config;
  return rest;
}

/**
 * Build the export file for the given step ids.
 * positions: where each step currently sits on the canvas (optional).
 */
export function buildStepsExport(
  ids: string[],
  nodes: DAGNode[],
  edges: DAGEdge[],
  secrets: SecretRef[],
  positions: Record<string, Pos> = {},
  sourceWorkflow?: string,
): StepsExport {
  const idSet = new Set(ids);
  const picked = nodes.filter(n => idSet.has(n.id));
  const placed = picked.map(n => positions[n.id] ?? n.position).filter(Boolean) as Pos[];
  const minX = placed.length ? Math.min(...placed.map(p => p.x)) : 0;
  const minY = placed.length ? Math.min(...placed.map(p => p.y)) : 0;

  const outNodes: DAGNode[] = picked.map(n => {
    const pos = positions[n.id] ?? n.position;
    const node: DAGNode = {
      id: n.id, label: n.label, type: n.type, config: cleanConfig(n.config),
      on_error: n.on_error, error_status: n.error_status,
    };
    if (pos) node.position = { x: pos.x - minX, y: pos.y - minY };
    return node;
  });
  const usedSecrets = referencedSecretNames(outNodes);
  return {
    format: STEPS_EXPORT_FORMAT,
    version: 1,
    exported_at: new Date().toISOString(),
    ...(sourceWorkflow ? { source_workflow: sourceWorkflow } : {}),
    nodes: outNodes,
    edges: edges.filter(e => idSet.has(e.source) && idSet.has(e.target)),
    secrets: secrets.filter(s => usedSecrets.has(s.name)),
  };
}

/**
 * Parse an uploaded file. Accepts a steps export, or a whole-workflow export
 * (format "catax-workflow", or a raw definition) — then every step is taken.
 */
export function parseStepsFile(text: string): StepsExport {
  let parsed: any;
  try { parsed = JSON.parse(text); } catch { throw new Error('not a JSON file'); }
  const src = parsed?.format === STEPS_EXPORT_FORMAT ? parsed
    : parsed?.format === 'catax-workflow' ? parsed.workflow
    : parsed;
  if (!src || !Array.isArray(src.nodes) || src.nodes.length === 0) throw new Error('no steps in this file');
  if (src.nodes.some((n: any) => !n || typeof n.id !== 'string' || !n.id || !n.type)) {
    throw new Error('a step is missing its id or type');
  }
  return {
    format: STEPS_EXPORT_FORMAT,
    version: 1,
    exported_at: parsed?.exported_at ?? '',
    source_workflow: parsed?.source_workflow ?? src.name,
    nodes: src.nodes,
    edges: Array.isArray(src.edges) ? src.edges : [],
    secrets: Array.isArray(src.secrets) ? src.secrets : [],
  };
}

export interface DanglingRef { nodeId: string; label: string; refs: string[] }

export interface ImportResult {
  nodes: DAGNode[];
  edges: DAGEdge[];
  idMap: Record<string, string>;       // old id → new id
  dangling: DanglingRef[];             // imported steps that still point outside the import
  secretsToAdd: SecretRef[];           // referenced secrets the target workflow lacks
  secretConflicts: string[];           // same name, different Secret Manager id in the target
}

/**
 * Give imported steps fresh ids and rewrite references between them.
 * nextId must return ids of the target workflow's own scheme (node_N);
 * any id already in the target is skipped.
 */
export function remapImportedSteps(
  data: StepsExport,
  existingNodes: DAGNode[],
  existingSecrets: SecretRef[],
  nextId: () => string,
  origin: Pos = { x: 0, y: 0 },
): ImportResult {
  const taken = new Set(existingNodes.map(n => n.id));
  const idMap: Record<string, string> = {};
  for (const n of data.nodes) {
    let id = nextId();
    while (taken.has(id)) id = nextId();
    taken.add(id);
    idMap[n.id] = id;
  }

  const stamp = Date.now();
  const nodes: DAGNode[] = data.nodes.map((n, i) => ({
    id: idMap[n.id],
    label: n.label,
    type: n.type,
    config: rewriteNodeRefs(cleanConfig(n.config), idMap),
    on_error: n.on_error ?? 'abort',
    error_status: n.error_status,
    position: {
      x: origin.x + (n.position?.x ?? 0),
      y: origin.y + (n.position?.y ?? i * 150),
    },
  }));
  const edges: DAGEdge[] = data.edges
    .filter(e => idMap[e.source] && idMap[e.target])
    .map((e, i) => ({
      ...e,
      id: `e_${idMap[e.source]}_${idMap[e.target]}_${stamp}_${i}`,
      source: idMap[e.source],
      target: idMap[e.target],
      ...(e.condition ? { condition: rewriteNodeRefs(e.condition, idMap) } : {}),
    }));

  const newIds = new Set(Object.values(idMap));
  const dangling: DanglingRef[] = [];
  for (const n of nodes) {
    const outside = Array.from(referencedNodeIds(n.config)).filter(id => !newIds.has(id));
    if (outside.length) dangling.push({ nodeId: n.id, label: n.label || n.type, refs: outside.sort() });
  }

  const have = new Map(existingSecrets.map(s => [s.name, s]));
  const secretsToAdd: SecretRef[] = [];
  const secretConflicts: string[] = [];
  for (const s of data.secrets) {
    const cur = have.get(s.name);
    if (!cur) { secretsToAdd.push(s); have.set(s.name, s); }
    else if (cur.secret !== s.secret) secretConflicts.push(s.name);
  }
  return { nodes, edges, idMap, dangling, secretsToAdd, secretConflicts };
}

/** Save JSON as a file download in the browser. */
export function downloadJson(filename: string, data: unknown): void {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'steps';
